import 'videojs-contrib-ads';
import { VASTClient, VASTTracker } from '@dailymotion/vast-client';
import {
  injectScriptTag, getLocalISOString, convertTimeOffsetToSeconds, isHttpUrl,
} from './lib';
import { playLinearAd, playNonLinearAd, playCompanionAd } from './modes';
import { addIcons, handleVMAP, parseInlineVastData } from './features';
let globalCurrentAd = null;

// VMAP time offsets of a preroll and a postroll. Every other break is a mid-roll, so no offset
// can be both (a 00:00:00 preroll would play again as a mid-roll, a 00:00:00.000 one not at all).
const PREROLL_OFFSETS = ['start', '0%', '00:00:00', '00:00:00.000'];
const POSTROLL_OFFSETS = ['end', '100%'];

class Vast {
  constructor(player, options) {
    this.player = player;

    // Load the options with default values
    const defaultOptions = {
      vastUrl: false,
      vmapUrl: false,
      verificationTimeout: 2000,
      addCtaClickZone: true,
      addSkipButton: true,
      debug: false,
      timeout: 5000,
      isLimitedTracking: false,
      isLive: false,
      isLiveSetFirstTime: false,
      prerollCurrentAd: {},
      isIos: false,
    };

    // Assign options that were passed in by the consumer
    this.options = Object.assign(defaultOptions, options);

    this.setMacros();

    // Init an empty array that will later contain the ads metadata
    this.adsArray = [];

    // array of nonlinear or companions dom element
    this.domElements = [];
    // array of icons dom containers
    this.iconContainers = [];

    // Ads whose impression has been sent, by whichever of their creatives showed first.
    this.impressedAds = new WeakSet();

    const videojsContribAdsOptions = {
      debug: this.options.debug,
      timeout: this.options.timeout,
      // Let video.js run its own autoplay attempt instead of terminating the first play request.
      // A terminated play never settles its promise, so `autoplay: 'any'` (PlayerConfigs) would
      // never reach its muted retry and never announce an outcome: on a browser that blocks
      // unmuted autoplay the content would sit paused whenever the ad break ends up empty, and
      // UnmuteNotice would have no 'autoplay-success' to hook. The cost is that the content is
      // no longer held back while the VMAP request is in flight.
      allowVjsAutoplay: true,
      // Never play a live stream muted behind the ad. contrib-ads does that wherever video.js
      // sees neither iOS nor Android - a Mac too, which isIos counts as iOS and so gives these
      // options rather than adsOptions - and then has no snapshot to restore when the ad ends:
      // it throws, and the stream never comes back.
      liveCuePoints: false,
    };

    const adsOptions = {
      ...videojsContribAdsOptions,
      contentIsLive: true,
    };

    // initialize videojs-contrib-ads
    if (!this.player.ads) return;
    try {
      this.player.ads(this.options.isLive && !this.options.isIos ? adsOptions : videojsContribAdsOptions);
    } catch (e) {
      console.error(e);
    }

    if (options.vmapUrl) {
      this.handleVMAP(options.vmapUrl);
    } else {
      this.disablePostroll();
      (async () => {
        await this.handleVAST(options.vastUrl, () => {
          this.disablePreroll();
        });
        if (this.adsArray.length > 0) {
          this.addEventsListeners();
          // has to be done outside of handleVAST because not done at the same moment for VMAP case
          this.player.trigger('adsready');
        }
      })();
    }
  }

  disablePreroll() {
    this.player.trigger('nopreroll');
  }

  // The VMAP could not be used (request failed, bad XML, no ad breaks). contrib-ads is already
  // initialized and would otherwise hold the content for the whole preroll timeout on play, and
  // again for the postroll timeout at the end, waiting for ads that will never come.
  disableAllAds() {
    this.disablePostroll();
    this.disablePreroll();
  }

  disablePostroll() {
    this.player.on('readyforpostroll', () => {
      this.player.trigger('nopostroll');
    });
  }

  setMacros(newMacros = undefined) {
    const { options } = this;
    if (!newMacros) {
      // generate unique int from current timestamp
      const cacheBuster = parseInt(Date.now().toString().slice(-8), 10);
      const ts = getLocalISOString(new Date());
      this.macros = {
        CACHEBUSTING: cacheBuster,
        TIMESTAMP: ts,
        PAGEURL: (window.location !== window.parent.location)
          ? document.referrer
          : document.location.href,
        // PODSEQUENCE: '',
        // UNIVERSALADID: '',
        // ADTYPE: '',
        // ADSERVINGID: '',
        // ADCATEGORIES: '',
        LIMITADTRACKING: options.isLimitedTracking,
      };
    } else {
      this.macros = {
        ...this.macros,
        ...newMacros,
      };
    }
  }

  async handleVAST(vastUrl, onError = null) {
    // Now let's fetch some adsonp
    this.vastClient = new VASTClient();
    try {
      const response = await this.vastClient.get(vastUrl, {
        allowMultipleAds: true,
        resolveAll: true,
      });
      const ads = response.ads ?? [];
      // Append rather than replace: a single ad break can be fed by more than one VAST (a VMAP
      // with several preroll ad breaks), and getNextAd() reads from the front, so ads that are
      // already queued have to survive.
      this.adsArray.push(...ads);
      if (ads.length === 0) {
        onError?.();
        // Deal with the error
        const message = 'VastVjs: Empty VAST XML';
        this.player.trigger('vast.error', {
          message,
          tag: vastUrl,
        });
        this.removeDomElements();
      }
    } catch (err) {
      console.error(err);
      onError?.();
      // Deal with the error
      const message = 'VastVjs: Error while fetching VAST XML';
      this.player.trigger('vast.error', {
        message,
        tag: vastUrl,
      });
      this.removeDomElements();
    }
  }

  removeDomElements() {
    // remove icons
    this.domElements.forEach((domElement) => {
      domElement.remove();
    });
    // These belong to the ad we just left, the next one pushes its own.
    this.domElements = [];
  }

  readAd() {
    // The countdown of the ad we are leaving has to be stopped here: only the newest handle is
    // kept in this.skipInterval, so the next addSkipButton() would overwrite it and leave the
    // old one ticking on a detached button for the rest of the session.
    this.clearSkipInterval();
    this.skipButtonAdded = false;

    const currentAd = this.getNextAd();
    if (!currentAd) {
      // Nothing left to play (every ad of the break failed, or an extra readyforpreroll came
      // in). Hand control back to the content instead of throwing on a null ad.
      if (this.player.ads.inAdBreak()) {
        this.resetPlayer();
      } else {
        this.player.ads.skipLinearAdMode();
      }
      return;
    }
    // to use for skip btn
    globalCurrentAd = currentAd;

    // Retrieve the CTA URl to render
    this.ctaUrl = Vast.getBestCtaUrl(currentAd.linearCreative());
    this.debug('ctaUrl', this.ctaUrl);

    if (currentAd.hasLinearCreative()) {
      this.linearVastTracker = new VASTTracker(
        this.vastClient,
        currentAd.ad,
        currentAd.linearCreative(),
      );
      this.linearVastTracker.on('firstQuartile', () => {
        this.debug('firstQuartile');
      });
      this.linearVastTracker.on('midpoint', () => {
        this.debug('midpoint');
      });
      this.addIcons(currentAd);
      // add it to adplaying to handle in live mode
      if (!this.options.isLive) this.addSkipButton(currentAd.linearCreative());
      // We now check if verification is needed or not, if it is, then we import the
      // verification script with a timeout trigger. If it is not, then we simply display the ad
      // by calling playAd
      if ('adVerifications' in currentAd.ad && currentAd.ad.adVerifications.length > 0) {
        // Set a timeout for the verification script - accortding to the IAB spec, we should do
        // a best effort to load the verification script before the actual ad, but it should not
        // block the ad nor the video playback
        // Scripts that finish after the timeout must not start the ad a second time.
        let started = false;
        const verificationTimeout = setTimeout(() => {
          started = true;
          this.playLinearAd(currentAd.linearCreative());
        }, this.options.verificationTimeout);

        // Now for each verification script, we need to inject a script tag in the DOM and wait
        // for it to load
        let index = 0;
        this.setMacros({
          OMIDPARTNER: `${currentAd.ad.adVerifications[index].vendor ?? 'unknown'}`,
        });
        const scriptTagCallback = () => {
          index += 1;
          if (index < currentAd.ad.adVerifications.length) {
            injectScriptTag(
              currentAd.ad.adVerifications[index].resource,
              scriptTagCallback,
              // eslint-disable-next-line no-use-before-define
              scriptTagErrorCallback,
            );
          } else {
            // Once we are done with all verification tags, clear the timeout timer and play the ad
            clearTimeout(verificationTimeout);
            if (!started) this.playLinearAd(currentAd.linearCreative());
          }
        };
        const scriptTagErrorCallback = () => {
          // track error
          this.linearVastTracker.verificationNotExecuted(
            currentAd.ad.adVerifications[index].vendor,
            { REASON: 3 },
          );
          // load next script
          scriptTagCallback();
        };
        injectScriptTag(
          currentAd.ad.adVerifications[index].resource,
          scriptTagCallback,
          scriptTagErrorCallback,
        );
      } else {
        // No verification to import, just run the add
        if (this.options.isLive) {
          this.options.prerollCurrentAd = currentAd.linearCreative();
        }
        this.playLinearAd(currentAd.linearCreative());
      }
    } else {
      // Nothing to play in the player: the next ad, or the end of the break. skipLinearAdMode()
      // is ignored once the break has started, which froze the player on the previous ad.
      this.readAd();
    }
    if (currentAd.hasNonlinearCreative()) {
      // TODO: remove those listeners
      this.player.one(currentAd.hasLinearCreative() ? 'adplaying' : 'playing', () => {
        this.nonLinearVastTracker = new VASTTracker(this.vastClient, currentAd.ad, currentAd.nonlinearCreative(), 'NonLinearAd');
        this.playNonLinearAd(currentAd.nonlinearCreative());
      });
    }
    if (currentAd.hasCompanionCreative()) {
      // TODO: remove those listeners
      this.player.one(currentAd.hasLinearCreative() ? 'adplaying' : 'playing', () => {
        this.companionVastTracker = new VASTTracker(this.vastClient, currentAd.ad, currentAd.companionCreative(), 'CompanionAd');
        this.playCompanionAd(currentAd.companionCreative());
      });
    }
  }

  /*
  * This method is responsible for retrieving the next ad to play from all the ads present in the
  * VAST manifest.
  * Please be aware that a single ad can have multple types of creatives.
  * A linear add for example can come with a companion ad and both can should be displayed.
  */
  getNextAd() {
    if (this.adsArray.length === 0) {
      return null;
    }
    const nextAd = this.adsArray.shift();
    return {
      ad: nextAd,
      hasLinearCreative: () => {
        // A linear creative with at least one media file
        const linear = nextAd.creatives.find((creative) => creative.type === 'linear');
        return !!linear?.mediaFiles?.some((mediaFile) => mediaFile.fileURL);
      },
      linearCreative: () => nextAd.creatives.filter((creative) => creative.type === 'linear')[0],
      hasCompanionCreative: () => nextAd.creatives.find((creative) => creative.type === 'companion') !== undefined,
      companionCreative: () => nextAd.creatives.filter((creative) => creative.type === 'companion')[0],
      hasNonlinearCreative: () => nextAd.creatives.find((creative) => creative.type === 'nonlinear') !== undefined,
      nonlinearCreative: () => nextAd.creatives.filter((creative) => creative.type === 'nonlinear')[0],
    };
  }

  // While contrib-ads waits for the preroll the content plays (allowVjsAutoplay) and its events
  // reach these handlers with the 'ad' prefix too. Only an ad break has an ad to track.
  onAdPlay = () => {
    if (!this.player.ads.inAdBreak()) return;
    this.debug('adplay');
    // 'adplaying' also follows every pause and rebuffer: one button per ad.
    if (this.options.isLive && !this.skipButtonAdded) {
      this.skipButtonAdded = true;
      this.addSkipButton(globalCurrentAd.linearCreative());
    }
    // don't track the very first play to avoid sending resume tracker event
    if (parseInt(this.player.currentTime(), 10) > 0) {
      this.linearVastTracker.setPaused(false, {
        ...this.macros,
        ADPLAYHEAD: this.linearVastTracker.convertToTimecode(this.player.currentTime()),
      });
    }
  };

  onAdPause = () => {
    if (!this.player.ads.inAdBreak()) return;
    this.debug('adpause');
    this.player.trigger('vast.pause', {
      ctaUrl: this.ctaUrl,
      skipDelay: this.linearVastTracker.skipDelay,
      adClickCallback: this.ctaUrl ? () => this.adClickCallback(this.ctaUrl) : false,
    });
    // don't track the pause event triggered before complete
    if (this.player.duration() - this.player.currentTime() > 0.2) {
      this.linearVastTracker.setPaused(true, {
        ...this.macros,
        ADPLAYHEAD: this.linearVastTracker.convertToTimecode(this.player.currentTime()),
      });
    }
  };

  // Track timeupdate-related events
  onAdTimeUpdate = () => {
    if (!this.player.ads.inAdBreak()) return;
    if (this.options.isLive && !this.options.isIos) {
      this.player.duration(this.player.duration() > this.options.prerollCurrentAd.duration ?
        this.options.prerollCurrentAd.duration : this.player.duration());
    }
    if (this.player.currentTime() > this.player.duration() &&
      !this.options.isLiveSetFirstTime && this.options.isLive && !this.options.isIos) {
      // Not when muted is what the browser demanded in exchange for autoplaying (see the
      // 'autoplay-success' handler in VideoPlayer): unmuting without a click of the viewer's own
      // is refused, and Chrome pauses the video for trying. UnmuteNotice asks for that click.
      if (!this.player.cache_.autoplayNeededMuted) {
        this.player.muted(false);
      }
      this.player.currentTime(0);
      this.options.isLiveSetFirstTime = true;
    }
    // this.player.currentTime(this.player.currentTime() > this.macros.DURATION ? 0 : this.player.currentTime())
    // Set progress to track automated trackign events
    this.linearVastTracker.setProgress(this.player.currentTime(), this.macros);
    this.player.trigger('vast.time', {
      position: this.player.currentTime(),
      currentTime: this.player.currentTime(),
      duration: this.player.duration(),
    });
  };

  // track on regular content progress
  onProgress = async () => {
    if (this.watchForProgress && this.watchForProgress.length > 0) {
      const { timeOffset } = this.watchForProgress[0];
      const timeOffsetInSeconds = convertTimeOffsetToSeconds(timeOffset, this.player.duration());
      const showCounter = (timeOffsetInSeconds - this.player.currentTime() <= 10) && (timeOffsetInSeconds - this.player.currentTime() > 0);
      if (showCounter) {
        this.player.trigger({
          type: 'vast.showAdCounter',
          counter: timeOffsetInSeconds - this.player.currentTime(),
        });
      }
      if (this.player.currentTime() > timeOffsetInSeconds) {
        const nextAd = this.watchForProgress.shift();
        // Prerolls that came in after their timeout are still queued; they don't belong here.
        this.adsArray = [];
        if (nextAd.vastUrl) {
          await this.handleVAST(nextAd.vastUrl);
          this.readAd();
        } else if (nextAd.vastData) {
          this.parseInlineVastData(nextAd.vastData, 'midroll');
        }
      }
    }
  };

  onFirstPlay = () => {
    this.debug('first play');
    // Track the first timeupdate event - used for impression tracking
  };

  onAdVolumeChange = () => {
    this.debug('volume');
    if (!this.linearVastTracker) {
      return false;
    }
    // Track the user muting or unmuting the video
    this.linearVastTracker.setMuted(this.player.muted(), {
      ...this.macros,
      ADPLAYHEAD: this.linearVastTracker.convertToTimecode(this.player.currentTime()),
    });
    return true;
  };

  onAdFullScreen = (evt, data) => {
    this.debug('fullscreen');
    if (!this.linearVastTracker) {
      return false;
    }
    // Track skip event
    this.linearVastTracker.setFullscreen(data.state);
    return true;
  };

  // Track when user closes the video
  onUnload = () => {
    if (!this.linearVastTracker) {
      return false;
    }

    this.linearVastTracker.close({
      ...this.macros,
      ADPLAYHEAD: this.linearVastTracker.convertToTimecode(this.player.currentTime()),
    });
    this.removeEventsListeners();
    return null;
  };

  // Notify the player if we reach a timeout while trying to load the ad
  onAdTimeout = () => {
    this.debug('adtimeout');
    // trigger a tracker error
    if (this.linearVastTracker) {
      this.linearVastTracker.error({
        ...this.macros,
        ERRORCODE: 301, // timeout of VAST URI
      });
    }
    console.error('VastVjs: Timeout');
    this.player.trigger({
      code: 408,
      type: 'vast.error',
      message: 'VastVjs: Timeout',
    });
    this.removeDomElements();
    // Give up the preroll only, so one that comes in late can't start in the middle of the
    // content. Mid-rolls and the postroll stay.
    this.player.off('readyforpreroll', this.onReadyForPreroll);
  };

  // send event when ad is playing to remove loading spinner
  onAdStart = () => {
    this.debug('adstart');
    // Trigger an event to notify the player consumer that the ad is playing
    this.player.trigger('vast.play', {
      ctaUrl: this.ctaUrl,
      skipDelay: this.linearVastTracker.skipDelay,
      adClickCallback: this.ctaUrl ? () => this.adClickCallback(this.ctaUrl) : false,
    });
    // Track the impression of an ad
    this.linearVastTracker.load({
      ...this.macros,
      ADPLAYHEAD: this.linearVastTracker.convertToTimecode(this.player.currentTime()),
    });

    this.trackImpression(this.linearVastTracker, {
      ...this.macros,
      ADPLAYHEAD: this.linearVastTracker.convertToTimecode(this.player.currentTime()),
    });
    this.linearVastTracker.overlayViewDuration(
      this.linearVastTracker.convertToTimecode(this.player.currentTime()),
      this.macros,
    );

    // make timeline not clickable
    if (this.player.controlBar) {
      this.player.controlBar.progressControl.disable();
    }

    if (this.options.addCtaClickZone) {
      // add the cta click
      const ctaDiv = document.createElement('div');
      ctaDiv.style.cssText = 'position: absolute; bottom:3em; left: 0; right: 0;top: 0;';
      ctaDiv.addEventListener('click', () => {
        this.player.pause();
        this.adClickCallback(this.ctaUrl);
      });
      this.domElements.push(ctaDiv);
      this.player.el().appendChild(ctaDiv);
    }
  };

  // Once per ad, whichever of its creatives shows first: vast-client guards each tracker on its
  // own, and an ad with a linear, a non-linear and a companion creative has three of them.
  trackImpression(tracker, macros) {
    if (this.impressedAds.has(tracker.ad)) return;
    this.impressedAds.add(tracker.ad);
    tracker.trackImpression(macros);
  }

  addSkipButton(creative) {
    this.debug('addSkipButton');
    if (this.options.addSkipButton && creative.skipDelay > 0) {
      const { skipDelay } = creative;
      let skipRemainingTime = Math.round(skipDelay - this.player.currentTime());
      let isSkippable = skipRemainingTime < 1;

      const svgString = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style="margin-left: 0.5rem"><path d="M2.86839 13.7592C1.83655 14.328 1 13.862 1 12.7191L1 4.28512C1 3.14109 1.83655 2.67568 2.86839 3.244L10.5384 7.47165C11.5706 8.04071 11.5706 8.96267 10.5384 9.5316L2.86839 13.7592Z" fill="#1C1C1C"/><path d="M12.9146 3.91667C12.9146 3.41041 13.325 3 13.8312 3C14.3375 3 14.7479 3.41041 14.7479 3.91667V13.0833C14.7479 13.5896 14.3375 14 13.8312 14C13.325 14 12.9146 13.5896 12.9146 13.0833V3.91667Z" fill="#1C1C1C"/></svg>';
      const parser = new DOMParser();
      const svgElement = parser.parseFromString(svgString, 'image/svg+xml').documentElement;
      const spanElement = document.createElement('span');
      spanElement.textContent = 'رد کردن آگهی';

      // add the skip button
      const skipButtonDiv = document.createElement('div');
      skipButtonDiv.id = 'videojs-vast-skipButton';
      skipButtonDiv.style.cssText = 'position: absolute; right: 1.5rem; bottom: 10.5rem; min-width: 9rem; width: 10.75rem; height:2.5rem; border-radius:0.5rem; z-index: 3; background: #f1f1f1; color:#1c1c1c; display: flex; justify-content: center; align-items: center; padding: 0.5rem 1rem; cursor: pointer; font-size: 0.875rem; font-weight: 500';
      skipButtonDiv.style.cursor = 'not-allowed';
      skipButtonDiv.innerHTML = 'رد کردن آگهی';

      // if (isSkippable) {
      //   skipButtonDiv.appendChild(spanElement);
      //   skipButtonDiv.appendChild(svgElement);
      // } else {
      //   skipButtonDiv.innerHTML = skipRemainingTime.toFixed();
      // }
      this.domElements.push(skipButtonDiv);
      this.player.el().appendChild(skipButtonDiv);
      // update time
      this.skipInterval = setInterval(() => {
        skipRemainingTime = Math.round(skipDelay - this.player.currentTime());
        isSkippable = skipRemainingTime < 1;
        if (isSkippable) {
          skipButtonDiv.style.cursor = 'pointer';
          skipButtonDiv.style.pointerEvents = 'auto';
          skipButtonDiv.addEventListener('click', () => {
            this.player.trigger('skip');
          });
          this.clearSkipInterval();
        }
        if (isSkippable) {
          skipButtonDiv.innerHTML = '';
          skipButtonDiv.appendChild(svgElement);
          skipButtonDiv.appendChild(spanElement);
        } else {
          const timeElement = document.createElement('span');
          timeElement.style.cssText = 'margin-right: 0.25rem';
          timeElement.innerHTML = `تا (${skipRemainingTime.toFixed()})`;
          skipButtonDiv.innerHTML = '';
          skipButtonDiv.appendChild(svgElement);
          skipButtonDiv.appendChild(spanElement);
          skipButtonDiv.appendChild(timeElement);
        }
      }, 1000);
    }
  }

  clearSkipInterval = () => {
    clearInterval(this.skipInterval);
  };

  onAdError = (evt) => {
    this.debug('aderror');
    // const error = this.player.error();
    // trigger a tracker error
    // No tracker yet when the error comes from the content while contrib-ads is still waiting
    // for the break - it is redispatched as aderror all the same.
    if (this.linearVastTracker) {
      this.linearVastTracker.error({
        ...this.macros,
        ERRORCODE: 900, // undefined error, to be improved
      });
    }

    this.removeDomElements();

    // no more ads (end of preroll, adpods or midroll)
    if (this.adsArray.length === 0) {
      // Only tell the consumer the ads are over once there is really nothing left to play.
      // Announcing it between the ads of a break makes it swap the ad UI back to the content
      // UI while the next ad of the same break is about to start.
      this.player.trigger('vast.error', {
        message: evt,
        tag: this.options.vastUrl,
      });
      this.resetPlayer();
    } else { // pods is not ended go ahead
      this.readAd();
    }
  };

  onReadyForPreroll = () => {
    this.debug('readyforpreroll');
    this.readAd();
  };

  onReadyForPostroll = async () => {
    this.debug('readyforpostroll');
    if (this.postRollUrl) {
      await this.handleVAST(this.postRollUrl);
      // Past the postroll timeout contrib-ads has already ended the content; the ad would play
      // outside ad mode, under the content controls.
      if (!this.player.ads.isWaitingForAdBreak()) return;
      this.readAd();
    } else if (this.postRollData) {
      // handle inline data
      this.adsArray = this.postRollData;
      this.readAd();
    }
  };

  onEnded = () => {
    this.removeEventsListeners();
  };

  onSkip = () => {
    this.debug('skip');

    // Trigger an event when the ad is finished to notify the player consumer
    this.player.trigger('vast.skip');

    // Track skip event
    this.linearVastTracker.skip({
      ...this.macros,
      ADPLAYHEAD: this.linearVastTracker.convertToTimecode(this.player.currentTime()),
    });

    // delete ctadiv, skip btn, icons, companions or nonlinear elements
    this.removeDomElements();

    // no more ads (end of preroll, adpods or midroll)
    if (this.adsArray.length === 0) {
      this.resetPlayer();
    } else {
      this.readAd();
    }
  };

  onDispose = () => {
    this.clearSkipInterval();
  };

  onAdEnded = () => {
    this.debug('adended');

    // Track the end of an ad
    this.linearVastTracker.complete({
      ...this.macros,
      ADPLAYHEAD: this.linearVastTracker.convertToTimecode(this.player.currentTime()),
    });

    // delete ctadiv, skip btn, icons, companions or nonlinear elements
    this.removeDomElements();

    // no more ads (end of preroll, adpods or midroll)
    if (this.adsArray.length === 0) {
      this.resetPlayer();
    } else { // pods is not ended go ahead
      this.readAd();
    }
  };

  resetPlayer() {
    // clear skip button interval
    this.clearSkipInterval();

    // Finish ad mode so that regular content can resume
    this.player.ads.endLinearAdMode();

    // Trigger an event when the ad is finished to notify the player consumer
    this.player.trigger('vast.complete');

    // reactivate controlbar
    if (this.player.controlBar) {
      this.player.controlBar.progressControl.enable();
    }
  }

  addEventsListeners() {
    // ad events
    this.player.one('adplaying', this.onFirstPlay);
    this.player.on('adplaying', this.onAdPlay);
    this.player.on('adpause', this.onAdPause);
    this.player.on('adtimeupdate', this.onAdTimeUpdate);
    this.player.on('advolumechange', this.onAdVolumeChange);
    this.player.on('adfullscreen', this.onAdFullScreen);
    this.player.on('adtimeout', this.onAdTimeout);
    this.player.on('adstart', this.onAdStart);
    this.player.on('aderror', this.onAdError);
    this.player.on('readyforpreroll', this.onReadyForPreroll);
    this.player.on('readyforpostroll', this.onReadyForPostroll);
    this.player.on('skip', this.onSkip);
    this.player.on('adended', this.onAdEnded);
    this.player.on('ended', this.onEnded);
    this.player.on('dispose', this.onDispose);
    window.addEventListener('beforeunload', this.onUnload);
  }

  removeEventsListeners() {
    this.debug('removeEventsListeners');
    this.player.off('adplaying', this.onAdPlay);
    this.player.off('adplaying', this.onFirstPlay);
    this.player.off('adpause', this.onAdPause);
    this.player.off('adtimeupdate', this.onAdTimeUpdate);
    this.player.off('advolumechange', this.onAdVolumeChange);
    this.player.off('adfullscreen', this.onAdFullScreen);
    this.player.off('adtimeout', this.onAdTimeout);
    this.player.off('adstart', this.onAdStart);
    this.player.off('aderror', this.onAdError);
    // added only if some midrolls have been found, remove by security
    this.player.off('timeupdate', this.onProgress);
    this.player.off('readyforpreroll', this.onReadyForPreroll);
    this.player.off('readyforpostroll', this.onReadyForPostroll);
    this.player.off('skip', this.onSkip);
    this.player.off('adended', this.onAdEnded);
    this.player.off('ended', this.onEnded);
    this.player.off('dispose', this.onDispose);
    window.removeEventListener('beforeunload', this.onUnload);
  }

  /*
  * This method is responsible for dealing with the click on the ad
  */
  adClickCallback = (ctaUrl) => {
    this.player.trigger('vast.click');
    window.open(ctaUrl, '_blank');
    // Track when a user clicks on an ad
    this.linearVastTracker.click(null, {
      ...this.macros,
      ADPLAYHEAD: this.linearVastTracker.convertToTimecode(this.player.currentTime()),
    });
  };

  static getCloseButton(clickCallback) {
    const closeButton = document.createElement('button');
    closeButton.addEventListener('click', clickCallback);
    closeButton.style.width = '20px';
    closeButton.style.height = '20px';
    closeButton.style.position = 'absolute';
    closeButton.style.right = '5px';
    closeButton.style.top = '5px';
    closeButton.style.zIndex = '3';
    closeButton.style.background = '#CCC';
    closeButton.style.color = '#000';
    closeButton.style.fontSize = '12px';
    closeButton.style.cursor = 'pointer';
    closeButton.textContent = 'X';
    return closeButton;
  }

  static applyNonLinearCommonDomStyle(domElement) {
    domElement.style.cursor = 'pointer';
    domElement.style.left = '50%';
    domElement.style.position = 'absolute';
    domElement.style.transform = 'translateX(-50%)';
    domElement.style.bottom = '80px';
    domElement.style.display = 'block';
    domElement.style.zIndex = '2';
  }

  /*
  * This method is responsible for choosing the best media file to play according to the user's
  * screen resolution and internet connection speed
  */
  static getBestMediaFile = (mediaFilesAvailable) => {
    // select the best media file based on internet bandwidth and screen size/resolution
    const videojsVhs = localStorage.getItem('videojs-vhs');
    const bandwidth = videojsVhs ? JSON.parse(videojsVhs).bandwidth : undefined;

    let bestMediaFile = mediaFilesAvailable[0];

    if (mediaFilesAvailable && bandwidth) {
      const { height } = window.screen;
      const { width } = window.screen;

      const result = mediaFilesAvailable
        .sort((a, b) => ((Math.abs(a.bitrate - bandwidth) - Math.abs(b.bitrate - bandwidth)) ||
          (Math.abs(a.width - width) - Math.abs(b.width - width)) ||
          (Math.abs(a.height - height) - Math.abs(b.height - height))));

      [bestMediaFile] = result;
    }

    return bestMediaFile;
  };

  /*
  * This method is responsible for choosing the best URl to redirect the user to when he clicks
  * on the ad
  */
  static getBestCtaUrl = (creative) => {
    const url = creative?.videoClickThroughURLTemplate?.url;
    return isHttpUrl(url) ? url : false;
  };

  static getMidrolls = (adBreaks) => {
    const midrolls = [];
    if (adBreaks) {
      return adBreaks
        .filter((adBreak) => !PREROLL_OFFSETS.includes(adBreak.timeOffset) && !POSTROLL_OFFSETS.includes(adBreak.timeOffset))
        .reduce((prev, current) => ([
          ...prev,
          {
            timeOffset: current.timeOffset,
            vastUrl: current.adSource.adTagURI?.uri,
            vastData: current.adSource.vastAdData,
          },
        ]), []);
    }
    return midrolls;
  };

  static getPreroll = (adBreaks) => {
    if (adBreaks) {
      return adBreaks.filter((adBreak) => PREROLL_OFFSETS.includes(adBreak.timeOffset));
    }
    return false;
  };

  static getPostroll = (adBreaks) => {
    if (adBreaks) {
      return adBreaks.filter((adBreak) => POSTROLL_OFFSETS.includes(adBreak.timeOffset))[0];
    }
    return false;
  };

  debug(msg, data = undefined) {
    if (!this.options.debug) {
      return;
    }
    console.info('videojs-vast ---', msg, data ?? '');
  }

  /*
  * This method is responsible disposing the plugin once it is not needed anymore
  */
  dispose() {
    this.debug('dispose');
    this.removeEventsListeners();
    super.dispose();
  }
}

Vast.prototype.playLinearAd = playLinearAd;
Vast.prototype.playNonLinearAd = playNonLinearAd;
Vast.prototype.playCompanionAd = playCompanionAd;
Vast.prototype.addIcons = addIcons;
Vast.prototype.handleVMAP = handleVMAP;
Vast.prototype.parseInlineVastData = parseInlineVastData;

export default Vast;

// Register the plugin with video.js
// The or statemente is necessary to deal with old versions of video.js
// const registerPlugin = videojs.registerPlugin || videojs.plugin;
// registerPlugin('vast', Vast);
